import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown, ArrowUp, Copy, FileText, Plus, Search, Sparkles, Trash2, X,
} from 'lucide-react';
import CareCard from '@/components/Lyrics/CareCard';
import PizzulMoves from '@/components/Lyrics/PizzulMoves';
import { careMatch, careTextForSong } from '@/lyrics/boothCare';
import { boothFetch } from '@/lyrics/boothClient';
import { copyText } from '@/lyrics/copyText';
import {
  countSyllables, lastWord, rhymeFamilyColor, rhymeRequestWords, swapLastWord,
} from '@/lyrics/editorHelpers';
import { stableId } from '@/lyrics/ids';
import { markerSections, overflowNotice } from '@/lyrics/markers';
import { parseBoothSongSheet } from '@/lyrics/pasteParser';
import { sectionAtPlayhead } from '@/lyrics/playhead';
import { fullSongSheet, songSheetLyrics } from '@/lyrics/songSheet';
import { fetchRhymes } from '@/lyrics/spApi';

function GrowingLine({ value, onChange, onFinish, onFocus }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = '0px';
    ref.current.style.height = `${ref.current.scrollHeight}px`;
  }, [value]);
  return <textarea
    ref={ref}
    className="lyrics-line-input"
    rows={1}
    maxLength={140}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    onFocus={onFocus}
    onKeyDown={(event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      event.currentTarget.blur();
      onFinish?.();
    }}
    aria-label="Lyric line"
  />;
}

function PocketStrip({ count }) {
  return <div className={`lyrics-pocket${count > 16 ? ' crowded' : ''}`} aria-label={`${count} syllables`}>
    {Array.from({ length: 16 }, (_, index) => <i key={index} className={index < count ? 'filled' : ''} />)}
  </div>;
}

function resultGroups(result) {
  const source = result?.groups || result || {};
  return [
    ['From you', source.from_you || source.fromYou || []],
    ['Perfect', source.perfect || []],
    ['Near', source.near || []],
    ['Multi', source.multi || source.multisyllable || []],
  ].map(([label, values]) => [label, Array.isArray(values) ? values : []]);
}

export const LyricsPanel = ({
  isOpen,
  onClose,
  lyrics,
  onChange,
  sectionMarkers,
  onAddParsedSong,
  kiosk = false,
  importOverflowCount = 0,
  undoResetKey,
  playheadBar = 0,
  totalBars = 0,
  isPlaying = false,
  aiDisabledCaption = '',
}) => {
  const [deleted, setDeleted] = useState(null);
  const [rhymeQuery, setRhymeQuery] = useState('');
  const [rhymeTarget, setRhymeTarget] = useState(null);
  const [rhymeResult, setRhymeResult] = useState(null);
  const [rhymeStatus, setRhymeStatus] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pastePreview, setPastePreview] = useState(null);
  const [pasteError, setPasteError] = useState(null);
  const [focusedLine, setFocusedLine] = useState(null);
  const [boothConfig, setBoothConfig] = useState(null);
  const [careKind, setCareKind] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');
  const deleteTimer = useRef(null);
  const careTimer = useRef(null);
  const copyTimer = useRef(null);
  const shownCareKinds = useRef(new Set());

  useEffect(() => {
    setDeleted(null);
    setFocusedLine(null);
    if (deleteTimer.current) clearTimeout(deleteTimer.current);
  }, [undoResetKey]);
  useEffect(() => () => {
    clearTimeout(deleteTimer.current);
    clearTimeout(careTimer.current);
    clearTimeout(copyTimer.current);
  }, []);

  useEffect(() => {
    if (!isOpen || aiDisabledCaption) return undefined;
    let live = true;
    boothFetch('config')
      .then((config) => { if (live) setBoothConfig(config); })
      .catch(() => { if (live) setBoothConfig(null); });
    return () => { live = false; };
  }, [aiDisabledCaption, isOpen]);

  const showCare = useCallback((kind) => {
    if (!kind || shownCareKinds.current.has(kind)) return;
    shownCareKinds.current.add(kind);
    setCareKind(kind);
  }, []);

  useEffect(() => {
    clearTimeout(careTimer.current);
    const phrases = boothConfig?.care_phrases || boothConfig?.phrases;
    if (!phrases) return undefined;
    careTimer.current = window.setTimeout(() => {
      showCare(careMatch(careTextForSong(lyrics), phrases));
    }, 800);
    return () => clearTimeout(careTimer.current);
  }, [boothConfig, lyrics, showCare]);

  const grouped = useMemo(
    () => sectionMarkers.length
      ? markerSections(sectionMarkers, lyrics.sections)
      : {
        onTimeline: lyrics.sections.length ? lyrics.sections : [{
          id: null, marker_id: null, label: 'Lyrics', lines: [], virtual: true,
        }],
        detached: [],
      },
    [sectionMarkers, lyrics.sections],
  );
  const totalLines = lyrics.sections.reduce((sum, section) => sum + section.lines.length, 0);
  const activeMarkerId = useMemo(
    () => isPlaying ? sectionAtPlayhead(sectionMarkers, playheadBar, totalBars) : null,
    [isPlaying, playheadBar, sectionMarkers, totalBars],
  );

  if (!isOpen) return null;

  const ensureSection = (section) => {
    const existing = lyrics.sections.find((candidate) => candidate.id && candidate.id === section.id)
      || lyrics.sections.find((candidate) => candidate.marker_id && candidate.marker_id === section.marker_id);
    if (existing) return existing;
    return {
      id: stableId('section'),
      marker_id: section.marker_id || null,
      label: section.label,
      lines: [],
    };
  };

  const replaceSection = (section, replacement) => {
    const existingIndex = lyrics.sections.findIndex((candidate) => (
      (section.id && candidate.id === section.id)
      || (section.marker_id && candidate.marker_id === section.marker_id)
    ));
    const sections = [...lyrics.sections];
    if (existingIndex >= 0) sections[existingIndex] = replacement;
    else sections.push(replacement);
    onChange({ ...lyrics, sections });
  };

  const updateLine = (section, lineIndex, text) => {
    const stored = ensureSection(section);
    const lines = [...stored.lines];
    lines[lineIndex] = { text };
    replaceSection(section, { ...stored, lines });
  };

  const replaceFocusedLine = (focus, text) => {
    const section = lyrics.sections.find((item) => (
      (focus.sectionId && item.id === focus.sectionId)
      || (focus.markerId && item.marker_id === focus.markerId)
    ));
    if (section?.lines?.[focus.lineIndex]) updateLine(section, focus.lineIndex, text);
  };

  const addLine = (section) => {
    const stored = ensureSection(section);
    if (stored.lines.length >= 16 || totalLines >= 48) return;
    replaceSection(section, { ...stored, lines: [...stored.lines, { text: '' }] });
  };

  const moveLine = (section, lineIndex, direction) => {
    const stored = ensureSection(section);
    const target = lineIndex + direction;
    if (target < 0 || target >= stored.lines.length) return;
    const lines = [...stored.lines];
    [lines[lineIndex], lines[target]] = [lines[target], lines[lineIndex]];
    replaceSection(section, { ...stored, lines });
  };

  const deleteLine = (section, lineIndex) => {
    const stored = ensureSection(section);
    const removed = stored.lines[lineIndex];
    replaceSection(section, { ...stored, lines: stored.lines.filter((_, index) => index !== lineIndex) });
    setDeleted({ section: stored, line: removed, lineIndex });
    if (deleteTimer.current) clearTimeout(deleteTimer.current);
    deleteTimer.current = setTimeout(() => setDeleted(null), 6000);
    if (
      focusedLine?.lineIndex === lineIndex
      && (focusedLine.sectionId === stored.id || focusedLine.markerId === stored.marker_id)
    ) setFocusedLine(null);
  };

  const undoDelete = () => {
    if (!deleted) return;
    const current = lyrics.sections.find((section) => section.id === deleted.section.id)
      || deleted.section;
    const lines = [...current.lines];
    lines.splice(Math.min(deleted.lineIndex, lines.length), 0, deleted.line);
    replaceSection(current, { ...current, lines });
    setDeleted(null);
    clearTimeout(deleteTimer.current);
  };

  const searchRhymes = async (word = rhymeQuery, target = rhymeTarget) => {
    const query = word.trim();
    if (!query) return;
    setRhymeQuery(query);
    setRhymeTarget(target);
    setRhymeStatus('loading');
    try {
      const result = await fetchRhymes(query, rhymeRequestWords(lyrics, kiosk || Boolean(aiDisabledCaption)));
      setRhymeResult(result);
      setRhymeStatus('ready');
    } catch (error) {
      setRhymeStatus('error');
    }
  };

  const chooseRhyme = async (word) => {
    if (!rhymeTarget) {
      await copyText(word);
      return;
    }
    const section = lyrics.sections.find((item) => (
      (rhymeTarget.sectionId && item.id === rhymeTarget.sectionId)
      || (rhymeTarget.markerId && item.marker_id === rhymeTarget.markerId)
    ));
    const line = section?.lines[rhymeTarget.lineIndex];
    if (section && line) updateLine(section, rhymeTarget.lineIndex, swapLastWord(line.text, word));
  };

  const copySong = async (text, label) => {
    if (!await copyText(text)) return;
    setCopyStatus(`${label} copied`);
    clearTimeout(copyTimer.current);
    copyTimer.current = window.setTimeout(() => setCopyStatus(''), 1400);
  };

  const renderSection = (section) => <section
    className={`lyrics-section${section.marker_id === activeMarkerId ? ' current' : ''}`}
    key={section.id || section.marker_id || section.label}
  >
    <div className="lyrics-section-heading">
      <h3>{section.label}</h3>
      <span>{section.lines.length}/16</span>
    </div>
    <div className="lyrics-lines">
      {section.lines.map((line, lineIndex) => {
        const syllables = countSyllables(line.text);
        const word = lastWord(line.text);
        return <div className="lyrics-line" key={`${section.id || section.marker_id}-${lineIndex}`}>
          <GrowingLine
            value={line.text}
            onChange={(text) => updateLine(section, lineIndex, text)}
            onFocus={() => setFocusedLine({
              sectionId: section.id || null,
              markerId: section.marker_id || null,
              lineIndex,
            })}
          />
          <div className="lyrics-line-meta">
            <span className="rhyme-family" style={{ background: rhymeFamilyColor(word) }} />
            <span>{syllables} syllable{syllables === 1 ? '' : 's'}</span>
            <PocketStrip count={syllables} />
          </div>
          <div className="lyrics-line-actions">
            <button
              title={word ? `Find rhymes for ${word}` : 'Add a last word first'}
              disabled={!word}
              onClick={() => searchRhymes(word, {
                sectionId: ensureSection(section).id,
                markerId: section.marker_id,
                lineIndex,
              })}
            ><Sparkles size={16} /><span>Rhyme</span></button>
            <button title="Move line up" onClick={() => moveLine(section, lineIndex, -1)}>
              <ArrowUp size={16} />
            </button>
            <button title="Move line down" onClick={() => moveLine(section, lineIndex, 1)}>
              <ArrowDown size={16} />
            </button>
            <button title="Delete line" onClick={() => deleteLine(section, lineIndex)}>
              <Trash2 size={16} />
            </button>
          </div>
        </div>;
      })}
    </div>
    <button
      className="lyrics-add-line"
      disabled={section.lines.length >= 16 || totalLines >= 48}
      onClick={() => addLine(section)}
    ><Plus size={16} /> Add line</button>
  </section>;

  return <aside data-testid="lyrics-panel" className="lyrics-panel" aria-label="Lyrics panel">
    <header className="lyrics-panel-header">
      <div><FileText size={19} /><h2>Lyrics</h2></div>
      <button data-testid="close-lyrics-panel" title="Close lyrics" onClick={onClose}><X size={18} /></button>
    </header>

    <div className="lyrics-panel-scroll">
      <div className="lyrics-copy-actions">
        <button onClick={() => copySong(songSheetLyrics(lyrics), 'Lyrics')}>
          <Copy size={16} /> Copy lyrics
        </button>
        <button onClick={() => copySong(fullSongSheet(lyrics), 'Everything')}>
          <Copy size={16} /> Copy everything
        </button>
        {copyStatus && <span role="status">{copyStatus}</span>}
      </div>
      <label className="lyrics-field-label" htmlFor="lyrics-title">Song title</label>
      <input
        id="lyrics-title"
        className="lyrics-title-input"
        maxLength={80}
        value={lyrics.title}
        onChange={(event) => onChange({ ...lyrics, title: event.target.value })}
      />
      <label className="lyrics-field-label" htmlFor="lyrics-style">Style</label>
      <textarea
        id="lyrics-style"
        className="lyrics-style-input"
        maxLength={400}
        value={lyrics.style}
        onChange={(event) => onChange({ ...lyrics, style: event.target.value })}
      />

      <div className="lyrics-paste">
        <button className="lyrics-secondary-button" onClick={() => setPasteOpen((open) => !open)}>
          Paste a song
        </button>
        {pasteOpen && <div className="lyrics-paste-box">
          <textarea
            aria-label="Paste a Booth song"
            placeholder="Paste Copy everything from The Booth"
            value={pasteText}
            onChange={(event) => setPasteText(event.target.value)}
          />
          {pasteError && <p role="alert">{pasteError}</p>}
          {!pastePreview ? <button onClick={() => {
            try {
              setPastePreview(parseBoothSongSheet(pasteText));
              setPasteError(null);
            } catch (error) {
              setPasteError('That song sheet could not be read. Check the section headings and try again.');
            }
          }}>Preview song</button> : <div className="lyrics-paste-preview">
            <p>{pastePreview.sections.length} section{pastePreview.sections.length === 1 ? '' : 's'} ready.</p>
            <button onClick={() => {
              onAddParsedSong(pastePreview);
              setPasteOpen(false);
              setPasteText('');
              setPastePreview(null);
            }}>Add these sections</button>
          </div>}
        </div>}
      </div>

      {importOverflowCount > 0 && <p className="lyrics-notice">
        {overflowNotice(importOverflowCount)}
      </p>}
      {!sectionMarkers.length && <p className="lyrics-hint">
        Add song markers (Intro, Verse, Hook) to split your lyrics by section.
      </p>}

      {grouped.onTimeline.map(renderSection)}
      {grouped.detached.length > 0 && <div className="lyrics-detached">
        <h2>Not on the timeline</h2>
        {grouped.detached.map(renderSection)}
      </div>}

      {kiosk || aiDisabledCaption ? <p className="lyrics-kiosk-ai-caption">
        {aiDisabledCaption || "Pizzul's AI is off in kiosk mode. Rhyme Brain still works."}
      </p> : <PizzulMoves
        config={boothConfig}
        focusedLine={focusedLine}
        onCare={showCare}
        onReplaceLine={replaceFocusedLine}
        sheet={lyrics}
      />}

      <section className="rhyme-brain">
        <h2>Rhyme Brain</h2>
        <div className="rhyme-search">
          <input
            aria-label="Rhyme search"
            maxLength={60}
            value={rhymeQuery}
            onChange={(event) => setRhymeQuery(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && searchRhymes()}
            placeholder="Search a word"
          />
          <button title="Search rhymes" onClick={() => searchRhymes()}><Search size={18} /></button>
        </div>
        {rhymeStatus === 'loading' && <p>Finding rhymes...</p>}
        {rhymeStatus === 'error' && <p role="alert">Rhyme Brain could not connect. Try again.</p>}
        {rhymeStatus === 'ready' && <div className="rhyme-groups">
          {resultGroups(rhymeResult).map(([label, words]) => words.length > 0 && <div key={label}>
            <h3>{label}</h3>
            <div>{words.map((item) => {
              const word = typeof item === 'string' ? item : item.word;
              return <button key={word} onClick={() => chooseRhyme(word)}>{word}</button>;
            })}</div>
          </div>)}
        </div>}
      </section>
    </div>

    {deleted && <div className="lyrics-undo-toast" role="status">
      <span>Line deleted</span><button onClick={undoDelete}>Undo</button>
    </div>}
    <CareCard kind={careKind} onClose={() => setCareKind(null)} />
  </aside>;
};

export default LyricsPanel;
